// src/lib/emtRepo.ts
import { db } from "./firebase";
import type { ApplicationSubmissionPayload } from './applications/applicationForm';
import { isHiddenDemoJob } from "./demoAccounts";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  limit,
  getCountFromServer,
} from "firebase/firestore";
import type { EMTProfile, Application, Conversation, Message } from "../types/firestore";
import type { Job } from "../lib/firestore/types";
import { sanitizeForFirestore } from "./firestore/sanitize";
import {
  getCertificationMatch,
  getEmtCertificationNames,
  getJobCertRequirements,
} from "./certificationMatching";
import { getJob } from "./firestore/jobs";

export async function getProfile(uid: string): Promise<EMTProfile | null> {
  if (!db) {
    console.error('[getProfile] Firestore not initialized');
    return null;
  }
  const snap = await getDoc(doc(db, "profiles", uid));
  if (!snap.exists()) return null;
  return { uid, ...(snap.data() as any) } as EMTProfile;
}

export async function upsertProfile(uid: string, data: Partial<EMTProfile>) {
  if (!db) {
    console.error('[upsertProfile] Firestore not initialized');
    throw new Error('Firestore is not initialized');
  }
  const cleaned = sanitizeForFirestore(data) as Partial<EMTProfile>;
  await setDoc(
    doc(db, "profiles", uid),
    { ...cleaned, role: "emt", updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function getJobs(limitCount = 10): Promise<Job[]> {
  if (!db) {
    console.error('[getJobs] Firestore not initialized');
    return [];
  }
  const q = query(
    collection(db, "jobs"),
    where("isOpen", "==", true), // Only show open jobs
    orderBy("createdAt", "desc"),
    limit(limitCount * 2) // Fetch more to account for filtering
  );
  const snap = await getDocs(q);
  const allJobs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Job[];
  
  // CRITICAL: Filter out jobs missing orgId (broken data), invalid jobs, expired jobs, and non-active status
  const now = new Date();
  const validJobs = allJobs.filter((job) => {
    if (isHiddenDemoJob(job)) return false;
    // Exclude invalid jobs
    if ((job as any).isInvalid === true) {
      return false;
    }
    // Exclude jobs missing orgId
    const hasOrgId = typeof job.orgId === "string" && job.orgId.trim().length > 0;
    if (!hasOrgId) {
      console.warn('[JOBS_BAD_DATA] Job missing orgId:', { jobId: job.id, title: job.title });
      return false;
    }
    // Exclude non-active status (status must be "active" or missing/undefined for backwards compatibility)
    const status = (job as any).status || (job.isOpen ? "active" : "closed");
    if (status !== "active") {
      return false;
    }
    // Exclude expired jobs (if expiresAt field exists)
    if ((job as any).expiresAt) {
      try {
        const expiresAt = (job as any).expiresAt?.toDate ? (job as any).expiresAt.toDate() : new Date((job as any).expiresAt);
        if (expiresAt && expiresAt < now) {
          return false; // Job has expired
        }
      } catch (err) {
        console.warn('[JOBS_BAD_DATA] Invalid expiresAt for job:', { jobId: job.id, expiresAt: (job as any).expiresAt });
      }
    }
    return true;
  });
  
  // Log if we found broken jobs
  const brokenJobs = allJobs.filter(j => !j.orgId || (j as any).isInvalid);
  if (brokenJobs.length > 0) {
    console.warn('[JOBS_BAD_DATA] Found jobs missing orgId or marked invalid:', brokenJobs.map(j => ({ id: j.id, title: j.title, orgId: j.orgId, isInvalid: (j as any).isInvalid })));
  }
  
  // Return only valid jobs, limited to requested count
  return validJobs.slice(0, limitCount);
}

export async function applyToJob(
  uid: string,
  jobId: string,
  answers?: Array<{ questionId: string; answer: string | number }>,
  applicationForm?: ApplicationSubmissionPayload
): Promise<Application> {
  if (import.meta.env.DEV) console.log('[APPLY] applyToJob called', { uid, jobId });
  
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  
  // Early validation: block apply if jobId is missing
  if (!jobId || typeof jobId !== 'string' || jobId.trim().length === 0) {
    console.error('[APPLY_BLOCKED_INVALID_JOB]', { jobId });
    throw new Error('Invalid job: jobId is missing');
  }
  
  // Get EMT profile for application data
  if (import.meta.env.DEV) console.log('[APPLY] Fetching EMT profile', { uid });
  const profile = await getProfile(uid);
  if (!profile) {
    console.error('[APPLY] Profile not found', { uid });
    throw new Error('EMT profile not found');
  }
  
  if (import.meta.env.DEV) console.log('[APPLY] Profile loaded', { 
    uid, 
    hasDisplayName: !!profile.displayName,
    hasFirstName: !!(profile as any).firstName,
    certsCount: Array.isArray((profile as any).certifications) ? (profile as any).certifications.length : 0
  });

  // Preflight against the live job doc (list cards may omit certificationRequirements).
  const liveJob = await getJob(jobId);
  if (!liveJob) {
    throw new Error('Job not found. It may have been removed.');
  }
  // Same "Documents Reviewed" gate applyToJob enforces server-side; this only saves a round trip.
  const { getDocumentReviewGate, describeDocumentReviewGate } = await import('./credentials/documentReviewGate');
  const gate = getDocumentReviewGate({ profile, job: liveJob as any });
  // A job whose employer accepts applications from people missing a required credential lets the
  // candidate through; applyToJob re-reads the same switch from the job and records what is missing.
  const { jobAcceptsMissingCredentials } = await import('./applications/missingCredentials');
  if (!gate.eligible && !jobAcceptsMissingCredentials(liveJob)) {
    // Same code the server would answer with, so retry() treats it as final and never re-sends it.
    const blocked = new Error(describeDocumentReviewGate(gate)) as Error & { code?: string; details?: unknown };
    blocked.code = 'failed-precondition';
    blocked.details = { reason: 'documents_not_reviewed', mode: gate.mode, requiredCertifications: gate.requiredCertifications, gaps: gate.gaps };
    throw blocked;
  }
  
  // Use new idempotent applyToJob function (it will fetch the job doc internally)
  const { applyToJob: applyToJobFn } = await import('./firestore/applications');
  
  if (import.meta.env.DEV) console.log('[APPLY] Calling applyToJob function', { jobId, emtId: uid });
  
  try {
    const applicationId = await applyToJobFn({
      jobId: jobId,
      emtId: uid,
      emtProfile: profile,
      answers,
      application: applicationForm,
    });
    
    if (import.meta.env.DEV) console.log('[APPLY] Application created successfully', { applicationId });
    
    // Get the created application (query fallback avoids permission edge cases on direct get)
    const { getApplication, getMyApplicationForJob } = await import('./firestore/applications');
    let application = await getApplication(applicationId).catch(() => null);
    if (!application) {
      application = await getMyApplicationForJob(uid, jobId);
    }
    if (!application) {
      console.warn('[APPLY] Application created but read-back empty; returning minimal payload', { applicationId });
      const { getDoc, doc } = await import('firebase/firestore');
      const jobDocRef = doc(db, 'jobs', jobId);
      const jobDoc = await getDoc(jobDocRef);
      const jobData = jobDoc.exists() ? jobDoc.data() : {};
      const jobTitle = typeof jobData.title === 'string' ? jobData.title : 'Job';
      const company = typeof jobData.company === 'string' ? jobData.company : 'Company';
      return {
        id: applicationId,
        jobId,
        jobTitle,
        company,
        status: 'new' as any,
      };
    }
    
    // Get job document to extract display fields
    const { getDoc, doc } = await import('firebase/firestore');
    const jobDocRef = doc(db, 'jobs', jobId);
    const jobDoc = await getDoc(jobDocRef);
    const jobData = jobDoc.exists() ? jobDoc.data() : {};
    
    // Note: Cloud Function (onApplicationCreate) will create notification automatically
    
    // Return application in legacy format for backward compatibility
    const jobTitle = typeof jobData.title === 'string' ? jobData.title : 
                     typeof (jobData.title as any)?.title === 'string' ? (jobData.title as any).title :
                     'Job';
    const company = typeof jobData.company === 'string' ? jobData.company :
                    typeof (jobData.company as any)?.name === 'string' ? (jobData.company as any).name :
                    'Company';
    
    return {
      id: applicationId,
      jobId: jobId,
      jobTitle,
      company,
      status: "new" as any, // Legacy status format
    };
  } catch (error: any) {
    console.error('[APPLY] applyToJob failed', {
      code: error.code,
      message: error.message,
      jobId: jobId,
      emtId: uid,
      error
    });
    throw error;
  }
}

export async function getDashboardCounts(uid: string) {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  try {
    // Prefer aggregation count (no document reads). Fallback: capped scan.
    let applicationsCount = 0;
    try {
      const activeQ = query(
        collection(db, "applications"),
        where("emtId", "==", uid),
        where("status", "not-in", ["archived", "rejected"])
      );
      const agg = await getCountFromServer(activeQ);
      applicationsCount = agg.data().count;
    } catch (aggErr) {
      console.warn('[getDashboardCounts] Active applications aggregate failed, using capped scan:', aggErr);
      const appsQuery = query(collection(db, "applications"), where("emtId", "==", uid), limit(400));
      const appsSnap = await getDocs(appsQuery);
      applicationsCount = appsSnap.docs.filter((d) => {
        const status = d.data().status;
        return status !== 'archived' && status !== 'rejected';
      }).length;
    }
    
    // Get interviews count from applications (real data)
    // Count applications with status "offered" (which represents interview/offer stage)
    const interviewsQuery = query(
      collection(db, "applications"),
      where("emtId", "==", uid),
      where("status", "==", "offered")
    );
    const interviewsSnap = await getCountFromServer(interviewsQuery);
    const interviewsCount = interviewsSnap.data().count;
    
    // Get profile for certifications
    const profileSnap = await getDoc(doc(db, "profiles", uid));
    const p = profileSnap.data() as any;
    const certs = Array.isArray(p?.professional?.certifications) 
      ? p.professional.certifications.length 
      : 0;
    
    // Profile views: employers write one record per org per day to publicProfiles/{uid}/views
    // (recordProfileView). Nothing has incremented publicProfiles.profileViewsCount since the
    // client increment was removed (employers cannot update that doc), so the tile read 0 forever.
    // Count the records themselves — the owner may read their own views, and an aggregation
    // query is exact and cheap. The old field is only a fallback.
    let views = 0;
    try {
      const countSnap = await getCountFromServer(collection(db, "publicProfiles", uid, "views"));
      views = countSnap.data().count;
    } catch (err) {
      console.warn('[getDashboardCounts] Failed to count profile views:', err);
      try {
        const publicProfileSnap = await getDoc(doc(db, "publicProfiles", uid));
        views = publicProfileSnap.exists() ? (publicProfileSnap.data()?.profileViewsCount ?? 0) : 0;
      } catch {
        views = p?.stats?.profileViews ?? 0;
      }
    }
    
    return {
      applications: applicationsCount,
      interviews: interviewsCount, // Real count from applications
      certifications: certs,
      profileViews: views,
    };
  } catch (err) {
    console.error('[getDashboardCounts] Error:', err);
    // Return safe defaults
    return {
      applications: 0,
      interviews: 0,
      certifications: 0,
      profileViews: 0,
    };
  }
}

// Conversations + messages
export async function getConversations(uid: string): Promise<Conversation[]> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  const q = query(
    collection(db, "conversations"),
    where("participants", "array-contains", uid),
    orderBy("lastMessageAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as any),
  })) as Conversation[];
}

export async function markNotificationRead(uid: string, notificationId: string) {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  await updateDoc(
    doc(db, "profiles", uid, "notifications", notificationId),
    { read: true }
  );
}

// Get unread notifications count
export async function getUnreadNotificationsCount(uid: string): Promise<number> {
  if (!db) {
    throw new Error('Firestore not initialized');
  }
  const q = query(
    collection(db, "profiles", uid, "notifications"),
    where("read", "==", false)
  );
  const snap = await getDocs(q);
  return snap.size;
}

// Get unread conversations count (conversations with unread messages)
export async function getUnreadConversationsCount(uid: string): Promise<number> {
  // For now, we'll count conversations with recent messages
  // This is a simplified version - you may want to track unread status per conversation
  const conversations = await getConversations(uid);
  // Count conversations with messages in the last 7 days that the user hasn't seen
  // For simplicity, we'll return the total count of conversations
  // You can enhance this later with proper unread tracking
  return conversations.length;
}

